import { createViewRequest, updateViewRequest } from 'modules/messagingInsights/actions';
import { MediumOptions, TabLabels, Tabs } from 'modules/messagingInsights/constants';
import {
  useCampaignAndMetricFiltersContext,
  useTabContext,
  useViewContext,
} from 'modules/messagingInsights/context';
import { selectSavedView } from 'modules/messagingInsights/selectors';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { Locked24, MultipleUsers24, WarningFill } from 'ui/icons';
import { InputText, TextArea } from 'ui/inputs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { BasicSelect } from 'ui/selects';
import { colors, spacing } from 'ui/theme';
import { useGetViewPayload } from '../hooks/useGetViewPayload';
import { useViewsModals } from '../hooks/useViewsModals';

const Label = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorLabel = styled.div`
  display: flex;
  align-items: center;
  > span {
    margin-left: ${spacing.quarter};
  }
`;

const Red = styled.span`
  color: ${colors.red100};
`;

const ShiftedSpan = styled.span`
  margin-left: ${spacing.quarter};
`;

const content = {
  createHeader: 'New saved view',
  editHeader: 'Edit saved view',
  name: (
    <span>
      Name <Red>*</Red>
    </span>
  ),
  defaultTab: 'Default tab',
  description: {
    label: 'Description',
  },
  access: 'Who can access',
  accessOpts: [
    {
      id: 'private',
      value: 'private',
      name: () => (
        <Label>
          <Locked24 /> <ShiftedSpan>Only me</ShiftedSpan>
        </Label>
      ),
      description: 'Only you can see and edit this view',
    },
    {
      id: 'public',
      value: 'public',
      name: () => (
        <Label>
          <MultipleUsers24 /> <ShiftedSpan>Anyone in this project</ShiftedSpan>
        </Label>
      ),
      description: 'Anyone in this project can see and edit this view',
    },
  ],
  saveView: 'Create saved view',
  saveChanges: 'Save view',
  cancel: 'Cancel',
  noNameError: (
    <ErrorLabel>
      <WarningFill width={12} height={12} color={colors.warning100} />
      <span>A name is required</span>
    </ErrorLabel>
  ),
  longNameError: (
    <ErrorLabel>
      <WarningFill width={12} height={12} color={colors.warning100} />
      <span>Name must be less than 256 characters</span>
    </ErrorLabel>
  ),
};

// Filter out Location + Devices from tab options when Email isn't a selected medium
const getTabOptions = (selectedMediums: string[]) => {
  const allTabs = Object.keys(Tabs);
  const includesEmail = selectedMediums.includes(MediumOptions.Email);
  const { location, devices } = Tabs;
  return (includesEmail ? allTabs : allTabs.filter((t) => ![location, devices].includes(t))).map(
    (tab) => ({
      id: tab,
      label: TabLabels[tab],
      value: tab,
    }),
  );
};

const getAccessOpts = (isPrivate: boolean, isEditMode: boolean) =>
  !isPrivate && isEditMode
    ? content.accessOpts.map((i) => ({ ...i, isDisabled: true }))
    : content.accessOpts;

export const CreateOrEditViewModal: FC = () => {
  const dispatch = useDispatch();
  const { selectedTab } = useTabContext();
  const { selectedMediums } = useCampaignAndMetricFiltersContext();
  const { toggleRefetchDropdownViews } = useViewContext();
  const tabOptions = getTabOptions(selectedMediums);

  // States used in Edit mode to pre-populate form with existing props
  const { edit: editModal, create: createModal } = useViewsModals();
  const isEditMode = editModal.isShown;
  const view = useSelector(selectSavedView) || {};
  const viewAccess = view.isPrivate ? 'private' : 'public';

  const [name, setName] = useState(isEditMode ? view.name : '');
  const isNameTooLong = name.length > 255;
  const [tab, setTab] = useState(isEditMode ? view.defaultTab : selectedTab);
  const [description, setDescription] = useState(isEditMode ? view.description : '');
  const [access, setAccess] = useState(isEditMode ? viewAccess : 'private');
  const metaData = { name, tab, description, isPrivate: access === 'private' };

  // Modal Actions
  const handleClose = () => (isEditMode ? editModal.toggle(false) : createModal.toggle(false));

  // Used for text field validation checks when a user attempts to save a new view/edit a view's properties
  const [saveAttempt, setSaveAttempt] = useState(false);

  // Reset dropdown when a view is created or updated
  const callback = () => {
    toggleRefetchDropdownViews();
  };

  const viewPayload = useGetViewPayload(metaData);
  const handleSave = () => {
    if (name && !isNameTooLong) {
      dispatch(
        isEditMode
          ? updateViewRequest({ ...view, ...viewPayload }, callback)
          : createViewRequest(viewPayload, callback),
      );
      handleClose();
      // Once the view selection dropdown has been created, ensure that
      // it is updated with the newly created/edited view listed and selected}
    } else {
      setSaveAttempt(true);
    }
  };

  return (
    <Modal
      id="create-view-modal"
      isModalOpen={true}
      onClose={handleClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{isEditMode ? content.editHeader : content.createHeader}</ModalHeader>
      <ModalBody>
        <FormField
          labelText={content.name}
          errorText={isNameTooLong ? content.longNameError : content.noNameError}
          hasError={isNameTooLong || (!name && saveAttempt)}
        >
          <InputText
            id="view-name"
            onChange={(viewName: string) => {
              setName(viewName);
            }}
            value={name}
            hasError={!name && saveAttempt}
            placeholder=""
            autoFocus
          ></InputText>
        </FormField>
        <FormField labelText={content.defaultTab}>
          <BasicSelect
            data-test="default-tab-select"
            onSelect={(tabs: any) => setTab(tabs[0])}
            options={tabOptions}
            defaultValues={[tab]}
            isFullWidth
          />
        </FormField>
        <FormField labelText={content.description.label}>
          <TextArea value={description} onChange={(x) => setDescription(x)} maxLength={255} />
        </FormField>
        <FormField labelText={content.access}>
          <RadioList
            name={content.access}
            onSelect={(val) => setAccess(val.value)}
            options={getAccessOpts(view.isPrivate, isEditMode)}
            value={access}
          />
        </FormField>
      </ModalBody>
      <ModalFooter cancelLabel={content.cancel}>
        <PrimaryButton onClick={handleSave} size={BUTTON_SIZES.LARGE}>
          {isEditMode ? content.saveChanges : content.saveView}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
